import React, { useState, useEffect } from "react";
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaUpload, FaCalendarAlt } from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../utils/axiosInstance"; // Use your custom axios instance if authentication is needed

const IMAGE_BASE_URL = process.env.REACT_APP_Image_URL || "https://api.solarestimate.in/image/";

export default function LeadView() {
  const { id: leadId } = useParams(); // Get lead ID from URL parameters
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [followUps, setFollowUps] = useState([]);
  const [followupCount, setFollowupCount] = useState(0);
  const [leadVisitCount, setLeadVisitCount] = useState(0);
  const [leadVisits, setLeadVisits] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  // Fetch lead details
  useEffect(() => {
    if (!leadId) return;

    axios.get(`/admin/api/lead/${leadId}`)
      .then(res => {
        if (res.data.Status === 1) {
          setLead(res.data.lead);
          // Map relative images to full URLs
          setAttachments((res.data.lead.images || []).map(img => `${IMAGE_BASE_URL}${img}`));
          setFollowUps(res.data.followUps || []);
          setFollowupCount(res.data.followupcount || 0);

          const visits = res.data.leadVisits || res.data.lead?.leadVisits || res.data.lead?.visits || [];
          setLeadVisits(visits);

          setLeadVisitCount(res.data.leadVisitcount || visits.length || 0);
        } else {
          console.error("Failed to fetch lead:", res.data.Message);
        }
      })
      .catch(console.error);
  }, [leadId]);

  useEffect(() => {
    if (window.bootstrap && window.bootstrap.Tooltip) {
      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      tooltipTriggerList.forEach(tooltipTriggerEl => {
        new window.bootstrap.Tooltip(tooltipTriggerEl);
      });
    }
  }, []);

  // File selection handler
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const totalFiles = attachments.length + files.length;
    if (totalFiles > 6) {
      alert(`Cannot select ${files.length} files. Maximum 6 files allowed. You currently have ${attachments.length} files.`);
      e.target.value = '';
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    const maxSize = 2 * 1024 * 1024;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!allowedTypes.includes(file.type)) {
        alert(`File "${file.name}" is not allowed. Only JPG, JPEG, PNG, and PDF files are accepted.`);
        e.target.value = '';
        return;
      }
      if (file.size > maxSize) {
        alert(`File "${file.name}" is too large. Maximum size is 2MB.`);
        e.target.value = '';
        return;
      }
    }

    const newPreviewUrls = files.map(file => file.type.startsWith('image/') ? URL.createObjectURL(file) : null);

    setSelectedFiles(files);
    setPreviewUrls(newPreviewUrls);
  };

  // File upload handler
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert("Please select files first");
      return;
    }

    // prevent double submission
    if (uploading) return;
    setUploading(true);

    const formData = new FormData();
    selectedFiles.forEach(file => {
      formData.append("images", file);
    });

    try {
      await axios.post(
        `/admin/api/lead/${leadId}/images`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      // Refresh lead data
      const res = await axios.get(`/admin/api/lead/${leadId}`);
      if (res.data.Status === 1) {
        setAttachments((res.data.lead.images || []).map(img => `${IMAGE_BASE_URL}${img}`));
      }

      alert("Upload successful");
      setSelectedFiles([]);
      setPreviewUrls([]);
      const fileInput = document.getElementById('attachments');
      if (fileInput) fileInput.value = '';
    } catch (err) {
      alert("Upload failed: " + (err.response?.data?.Message || err.message));
      console.error(err);
    }
    setUploading(false);
  };

  // Delete an existing attachment by filename or index
  const handleDeleteAttachment = async (imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;
    try {
      // imageUrl may be full URL; extract filename portion
      const filename = imageUrl.replace(/^.*\/(images\/)?/, '');
      // Backend expects removeImages array in POST body
      const payload = { removeImages: [filename] };
      const res = await axios.post(`/admin/api/lead/${leadId}/images`, payload, { headers: { 'Content-Type': 'application/json' } });
      if (res.data && res.data.Status === 1) {
        // Refresh attachments from API
        const updated = await axios.get(`/admin/api/lead/${leadId}`);
        if (updated.data.Status === 1) {
          setAttachments((updated.data.lead.images || []).map(img => `${IMAGE_BASE_URL}${img}`));
        }
      } else {
        alert('Failed to delete attachment');
      }
    } catch (err) {
      console.error('Delete failed', err);
      alert('Failed to delete attachment');
    }
  };

  const handleCancel = () => {
    setSelectedFiles([]);
    setPreviewUrls([]);
    const fileInput = document.getElementById('attachments');
    if (fileInput) fileInput.value = '';
  };

  if (!lead) return <div>Loading lead...</div>;

  return (
    <div className="container py-4" style={{ fontFamily: "inherit" }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold">Lead View</h3>
        <div className="d-flex align-items-center gap-2">
          <a href="/user/lead" className="text-decoration-none text-secondary">
            &larr; Lead
          </a>
        </div>
      </div>

      <div className="row g-3">
        {/* Lead Info Card */}
        <div className="col-md-6">
          <div className="card border-1 shadow-none p-4 rounded-3 h-100" style={{ background: "#CCCDC6" }}>
            <div className="d-flex align-items-center mb-3">
              <div
                className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center"
                style={{ width: 60, height: 60, fontSize: 24, fontWeight: 600 }}
              >
                {lead.name
                  ? lead.name.split(" ").map((n) => n[0]).join("")
                  : "-"}
              </div>
              <div className="ms-3">
                <h5 className="mb-1 fw-bold">{lead.name}</h5>
                <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 15 }}>
                  <FaEnvelope size={14} /> {lead.email}
                </div>
                <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 15 }}>
                  <FaPhone size={14} /> {lead.mobile}
                </div>
                <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 15 }}>
                  <FaMapMarkerAlt size={14} /> {lead.address}
                </div>
              </div>
            </div>
            <hr />
            <div>
              <h6 className="fw-semibold mb-3">Lead Info</h6>
              <div className="row">
                <div className="col-6">
                  <div className="mb-1 text-muted" style={{ fontSize: 15 }}>
                    <span className="me-3">Lead Owner: </span>
                    <span>{lead.leadOwner?.firstName && lead.leadOwner?.lastName ? `${lead.leadOwner.firstName} ${lead.leadOwner.lastName}` : 'Not Assigned'}</span>
                  </div>
                  <div className="mb-1 text-muted" style={{ fontSize: 15 }}>
                    <span className="me-3">Lead Created By: </span>
                    <span>{lead.createdBy?.firstName && lead.createdBy?.lastName ? `${lead.createdBy.firstName} ${lead.createdBy.lastName}` : ''}</span>
                  </div>
                  <div className="mb-1 text-muted" style={{ fontSize: 15 }}>
                    <span className="me-3">Lead Source: </span>
                    <span>{lead.leadSource?.name || lead.leadSource || ''}</span>
                  </div>
                </div>
                <div className="col-6">
                  <div className="mb-1 text-muted" style={{ fontSize: 15 }}>
                    <span className="me-3">Deal Stage: </span>
                    <span>{lead.dealStage?.name || lead.dealStage || ''}</span>
                  </div>
                  <div className="mb-1 text-muted" style={{ fontSize: 15 }}>
                    <span className="me-3">Deal Types: </span>
                    <span>{lead.dealType?.name || lead.dealType || ''}</span>
                  </div>
                  <div className="mb-1 text-muted" style={{ fontSize: 15 }}>
                    <span className="me-3">Deal Temperature: </span>
                    <span>{lead.dealTemprature?.name || lead.dealTemperature || ''}</span>
                  </div>
                  <div className="mb-1 text-muted" style={{ fontSize: 15 }}>
                    <span className="me-3">Pin Code: </span>
                    <span>{lead.pin_code}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Attachment Card */}
        <div className="col-md-6">
          <div className="card border-1 shadow-none p-4 rounded-3 h-100" style={{ background: "#CCCDC6" }}>
            <div className="mb-3">
              <label htmlFor="attachments" className="form-label">
                Other Attachments (Max 6)
                <span className="text-danger">*</span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm rounded-pill px-2 py-0 ms-2"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title="You may upload up to 6 files. Allowed formats: jpg, jpeg, png, pdf Max 2MB each."
                  style={{ fontSize: '12px', lineHeight: '1' }}
                >
                  ?
                </button>
              </label>
              <input
                id="attachments"
                className="form-control"
                type="file"
                name="attachments[]"
                multiple
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={handleFileSelect}
                disabled={uploading}
              />
              {selectedFiles.length > 0 && (
                <div className="text-muted small mt-1">
                  <i className="fas fa-check-circle me-1 text-success"></i>
                  {selectedFiles.length} file(s) selected
                </div>
              )}
              {uploading && (
                <div className="text-muted small mt-1">
                  <i className="fas fa-spinner fa-spin me-1"></i>
                  Uploading files...
                </div>
              )}
            </div>
            <div className="d-flex flex-wrap gap-2 justify-content-center mb-3">
              {/* Show existing attachments */}
              {attachments.length > 0 && attachments.map((imageUrl, idx) => (
                <div
                  key={`existing-${idx}`}
                  className="bg-light position-relative"
                  style={{
                    border: "1px solid #d9d9d9",
                    width: 80,
                    height: 80,
                    borderRadius: 7,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={imageUrl}
                    alt={`Attachment ${idx + 1}`}
                    style={{ maxWidth: "75px", maxHeight: "75px", borderRadius: "6px" }}
                  />
                  <button
                    className="btn btn-sm btn-danger position-absolute"
                    style={{ top: -6, right: -6, width: 22, height: 22, padding: 0, borderRadius: 12 }}
                    onClick={() => handleDeleteAttachment(imageUrl)}
                    title="Delete"
                  >
                    &times;
                  </button>
                </div>
              ))}
              {/* Show preview of selected files */}
              {previewUrls.length > 0 && previewUrls.map((url, idx) => (
                <div
                  key={`preview-${idx}`}
                  className="bg-light position-relative"
                  style={{
                    border: "2px solid #007bff",
                    width: 80,
                    height: 80,
                    borderRadius: 7,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {url ? (
                    <img
                      src={url}
                      alt={`Preview ${idx + 1}`}
                      style={{ maxWidth: "75px", maxHeight: "75px", borderRadius: "6px" }}
                    />
                  ) : (
                    <i className="fas fa-file-pdf" style={{ fontSize: '30px', color: '#666' }}></i>
                  )}
                  <button
                    className="btn btn-sm btn-danger position-absolute"
                    style={{ top: -6, right: -6, width: 22, height: 22, padding: 0, borderRadius: 12 }}
                    onClick={() => {
                      // remove the file and preview at idx
                      const newFiles = [...selectedFiles];
                      newFiles.splice(idx, 1);
                      setSelectedFiles(newFiles);
                      const newPreviews = [...previewUrls];
                      newPreviews.splice(idx, 1);
                      setPreviewUrls(newPreviews);
                    }}
                    title="Remove"
                  >
                    &times;
                  </button>
                  <span
                    className="position-absolute top-0 start-0 badge bg-primary"
                    style={{ fontSize: '9px' }}
                  >
                    New
                  </span>
                </div>
              ))}
              {/* Show empty placeholders */}
              {attachments.length + previewUrls.length < 6 &&
                Array(6 - attachments.length - previewUrls.length).fill("").map((_, idx) => (
                  <div
                    key={`placeholder-${idx}`}
                    className="bg-light"
                    style={{
                      border: "1px solid #d9d9d9",
                      width: 80,
                      height: 80,
                      borderRadius: 7,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {/* Placeholder square */}
                  </div>
                ))
              }
            </div>
            <div className="d-flex justify-content-end mt-3 gap-2">
              <button
                className="btn btn-light px-4"
                onClick={handleCancel}
                disabled={uploading || selectedFiles.length === 0}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary px-4"
                onClick={handleUpload}
                disabled={uploading || selectedFiles.length === 0}
                aria-disabled={uploading || selectedFiles.length === 0}
                style={{ opacity: (uploading || selectedFiles.length === 0) ? 0.6 : undefined, pointerEvents: uploading ? 'none' : undefined }}
              >
                {uploading ? (
                  <>
                    <i className="fas fa-spinner fa-spin me-1"></i>
                    Uploading...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* lead related count */}
      <div className="d-flex justify-content-between align-items-center my-4 px-1">
        <span className="d-flex align-items-center gap-2">
          <FaCalendarAlt
          title="Followups" 
          /><span>{followupCount}</span>
        </span>
        <span className="d-flex align-items-center gap-2">
          <FaMapMarkerAlt 
           title="Visit"/> <span>{leadVisitCount}</span>
        </span>
        <span className="d-flex align-items-center gap-2">
          <i className="fas fa-table"
          title="Estimate"></i> <span>{attachments.length}</span>
        </span>
      </div>
      {/* Lead Visits list (if any) */}
      {leadVisits && leadVisits.length > 0 && (
        <div className="my-4">
          <h5 className="fw-bold mb-3">Lead Visits</h5>
          <div className="row g-3">
            {leadVisits.map((v) => (
              <div key={v._id} className="col-md-4">
                <div className="card border-1 shadow-none p-4 rounded-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="fw-bold me-3 text-muted">
                      {v.selecteduser?.firstName ? `${v.selecteduser.firstName} ${v.selecteduser?.lastName || ''}` : 'Unknown'}
                    </div>
                    <div className="ms-auto text-muted small">{v.createdAt ? new Date(v.createdAt).toLocaleString() : ''}</div>
                  </div>
                  <div className="text-muted small mb-2">{v.selecteduser?.business?.name || ''}</div>
                  {v.note && <div className="mb-2">{v.note}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Follow-ups list (compact cards) */}
      {followUps && followUps.length > 0 && (
        <div className="my-4">
          <h5 className="fw-bold mb-3">Timeline</h5>
          <div className="card p-3 w-50" style={{ borderRadius: 8 }}>
            {followUps.map((f) => (
              <div key={f._id} className="d-flex align-items-start mb-3">
                <div style={{ width: 48, display: 'flex', justifyContent: 'center' }}>
                  <div style={{ width: 14, height: 14, borderRadius: 14, background: '#6c757d', marginTop: 6 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: '#333', fontWeight: 600 }}>{new Date(f.createdAt).toLocaleDateString()}</div>
                  <div style={{ color: '#666', fontSize: 13 }}>{f.note}</div>
                  <div style={{ color: '#999', fontSize: 12 }}>{f.nextdate ? `Add One ${new Date(f.nextdate).toLocaleString()}` : ''}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}